import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import EFilterButton from '@/components/everridge/EFilterButton';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { FilterHelper } from '@/utils/filterHelper';
import { cn } from '@/lib/utils';
import ArrowLeftIcon from '@/components/icons/ArrowLeftIcon';
import SuperAdminCatalogPageFilter from '@/features/superAdmin/features/data/shared/components/SuperAdminCatalogPageFilter';
import { useSuperAdminOwnersPageColumns } from '@/features/superAdmin/features/data/owners/components/SuperAdminOwnersPageColumns';
import { useSuperAdminOwnersFilter } from '@/features/superAdmin/features/data/owners/hooks/useSuperAdminOwnersFilter';
import { useSuperAdminOwnersListApi } from '@/features/superAdmin/features/data/owners/hooks/useSuperAdminOwnersListApi';
import { SuperAdminOwnerListItemDto } from '@/features/superAdmin/features/data/types/superAdmin-data.types';

const SuperAdminOwnersListPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const userRole = useUserRole();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
    const columns = useSuperAdminOwnersPageColumns();
    const { filter, setFilter, resetFilter } = useSuperAdminOwnersFilter();
    const [page, setPage] = useState(() => Math.floor(filter.skip / filter.take));
    const [showFilter, setShowFilter] = useState(false);
    const { list, isLoading } = useSuperAdminOwnersListApi({ suspendFetching: showFilter });

    useEffect(() => {
        const totalCount = list?.totalCount ?? 0;
        if ((totalCount === 0 && filter.skip > 0) || (totalCount > 0 && filter.skip >= totalCount)) {
            setFilter({ skip: 0 });
            setPage(0);
        }
    }, [list?.totalCount, filter.skip, setFilter]);

    useEffect(() => {
        setPage(Math.floor(filter.skip / filter.take));
    }, [filter.skip, filter.take]);

    if (userRole !== Roles.superAdmin) return null;

    const activeFiltersCount = FilterHelper.countFilterValues(filter, ['take', 'skip', 'orderBy', 'orderDirection']);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({ skip: filter.take * newPage });
    };

    const handleRowClick = (owner: SuperAdminOwnerListItemDto) => {
        navigate(`/superAdmin/data/owners/${owner.id}`, { state: { owner } });
    };

    return (
        <section className={cn('flex flex-1 flex-col gap-6', isMobile && 'gap-4')}>
            <EPageHeader title={t('superAdmin.data.owners.title')}>
                <Button variant="outline" className="gap-2" onClick={() => navigate('/superAdmin/data')}>
                    <ArrowLeftIcon className="h-4 w-4" />
                    {t('superAdmin.data.backToHub')}
                </Button>
                <EFilterButton
                    name={t('elements.components.filter.title')}
                    filtersQuantity={activeFiltersCount}
                    onClick={() => setShowFilter(true)}
                />
            </EPageHeader>

            <ETableWithApiPagination
                columns={columns}
                data={list}
                page={page}
                setPage={handlePageChange}
                paginationStep={filter.take}
                isLoadingPage={isLoading}
                onRowClick={handleRowClick}
            />

            {showFilter && (
                <SuperAdminCatalogPageFilter
                    onClose={() => setShowFilter(false)}
                    isMobile={isMobile}
                    filter={filter}
                    setFilter={setFilter}
                    resetFilter={resetFilter}
                />
            )}
        </section>
    );
};

export default SuperAdminOwnersListPage;
